import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, firstValueFrom } from 'rxjs';
import { JwtHelperService } from '@auth0/angular-jwt';

import { DomainService } from '../../domain/domain.service';
import { environment } from '../../../../../environments/environment';
import { ExternalToken } from '../../../dtos/core/auth/ExternalToken';
import { ResponseApi } from '../../../dtos/ResponseApi';
import { TokenResponse } from '../../../dtos/core/account/TokenResponse';

@Injectable({
  providedIn: 'root'
})
export class ExternalAuthService {

  private currentClienteSubject: BehaviorSubject<ExternalToken | null>;
  public currentCliente: Observable<ExternalToken | null>;

  private tokenKey = '';
  private readonly baseUrl = `${environment.apiUrl}/core/cliente/token`;
  private refreshTimer: any;
  private tokenRequestPromise: Promise<void> | null = null;

  constructor(
    private http: HttpClient,
    private domainService: DomainService,
    private jwtHelper: JwtHelperService
  ) {
    const slug = this.domainService.subdomain;
    this.tokenKey = slug ? `jwt_public_${slug}` : 'jwt_public';

    const storedToken = localStorage.getItem(this.tokenKey);
    this.currentClienteSubject = new BehaviorSubject<ExternalToken | null>(
      this.buildFromToken(storedToken)
    );

    this.currentCliente = this.currentClienteSubject.asObservable();
  }

  public get currentClienteValue(): ExternalToken | null {
    return this.currentClienteSubject.value;
  }

  shouldBlockStartup(): boolean {
    const slug = this.domainService.subdomain;

    if (!slug || this.domainService.isInternal) {
      return false;
    }

    const currentToken = this.currentClienteValue;
    if (!currentToken) {
      return true;
    }

    return this.isExpired(currentToken.expiration);
  }

  async loadToken(): Promise<void> {
    const slug = this.domainService.subdomain;
    const isInternal = this.domainService.isInternal;

    if (!slug || isInternal) return;

    this.tokenKey = `jwt_public_${slug}`;
    const saved = localStorage.getItem(this.tokenKey);

    if (saved) {
      const decoded = this.buildFromToken(saved);

      if (decoded && !this.isExpired(decoded.expiration)) {
        this.currentClienteSubject.next(decoded);
        this.scheduleRefresh(decoded.expiration);
        return;
      }
    }

    await this.fetchToken(slug);
  }

  private async fetchToken(slug: string): Promise<void> {
    if (this.tokenRequestPromise) {
      await this.tokenRequestPromise;
      return;
    }

    this.tokenRequestPromise = this.requestNewToken(slug)
      .finally(() => {
        this.tokenRequestPromise = null;
      });

    await this.tokenRequestPromise;
  }

  private async requestNewToken(slug: string): Promise<void> {
    try {
      const response = await firstValueFrom(
        this.http.get<ResponseApi<TokenResponse>>(`${this.baseUrl}/${slug}`)
      );

      if (!response.success) return;

      const token = response.data.accessToken;

      localStorage.setItem(this.tokenKey, token);

      const decoded = this.buildFromToken(token);
      if (!decoded) return;

      this.currentClienteSubject.next(decoded);
      this.scheduleRefresh(decoded.expiration);

    } catch (err) {
      console.error('[ExternalAuth] Erro buscando token:', err);
    }
  }

  private scheduleRefresh(exp: number) {
    const expiresAt = exp * 1000;
    const now = Date.now();
    const refreshInMs = expiresAt - now - (10 * 60 * 1000);

    if (refreshInMs <= 0) {
      this.refresh();
      return;
    }

    if (this.refreshTimer) clearTimeout(this.refreshTimer);

    this.refreshTimer = setTimeout(() => this.refresh(), refreshInMs);
  }

  async refresh(): Promise<void> {
    const slug = this.domainService.subdomain;
    if (!slug) return;

    await this.fetchToken(slug);
  }

  async getTokenForRequest(): Promise<string | null> {
    let currentToken = this.currentClienteValue;

    if (!currentToken) {
      await this.loadToken();
      currentToken = this.currentClienteValue;
      return currentToken?.token ?? null;
    }

    if (!this.isExpired(currentToken.expiration)) {
      return currentToken.token;
    }

    await this.refresh();
    return this.currentClienteValue?.token ?? null;
  }

  clearTokens() {
    Object.keys(localStorage)
      .filter(k => k.startsWith('jwt_public_'))
      .forEach(k => localStorage.removeItem(k));

    this.currentClienteSubject.next(null);

    if (this.refreshTimer) clearTimeout(this.refreshTimer);
  }

  private buildFromToken(token: string | null): ExternalToken | null {
    if (!token) return null;

    try {
      const decoded = this.jwtHelper.decodeToken(token);

      return {
        token,
        expiration: decoded['exp'],
        clienteId: decoded['ClienteId'],
        nomeFantasia: decoded['NomeFantasia'],
        slug: decoded['Slug'],
        portalUrl: decoded['PortalUrl'],
        codigoSigem: decoded['CodigoSigem'],
        codigoSiga: decoded['CodigoSiga']
      };

    } catch {
      return null;
    }
  }

  private isExpired(exp: number): boolean {
    const nowSec = Math.floor(Date.now() / 1000);
    return exp <= nowSec;
  }
}
